import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DEFAULT_SUMMARY_MODEL } from "@agentdash/shared";
import { api } from "../../lib/api-client.js";
import { Timestamp } from "../../components/Timestamp.js";

const TOKEN_KEY = ["settings", "github-token"] as const;
const DOKPLOY_KEY = ["settings", "dokploy"] as const;
const ANTHROPIC_KEY = ["settings", "anthropic"] as const;
const AGENTS_KEY = ["admin", "agents"] as const;

export function Settings() {
  return (
    <div>
      <header
        style={{
          padding: "12px 20px",
          color: "var(--muted)",
          fontSize: 12,
        }}
      >
        Configure integrations and register host agents. All changes take
        effect on the next dashboard refresh.
      </header>
      <GithubTokenPanel />
      <DokployPanel />
      <AnthropicPanel />
      <AgentsPanel />
    </div>
  );
}

// --- Anthropic (run summarization) --------------------------------------

function AnthropicPanel() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ANTHROPIC_KEY,
    queryFn: () => api.getAnthropicSetting(),
  });

  const save = useMutation({
    mutationFn: ({
      api_key,
      model,
      enabled,
    }: {
      api_key: string;
      model: string;
      enabled: boolean;
    }) => api.setAnthropicSetting(api_key, model, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ANTHROPIC_KEY }),
  });
  const remove = useMutation({
    mutationFn: () => api.deleteAnthropicSetting(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ANTHROPIC_KEY }),
  });

  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [model, setModel] = useState(DEFAULT_SUMMARY_MODEL);
  const [enabled, setEnabled] = useState(true);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (value.trim().length < 20) return;
    await save.mutateAsync({
      api_key: value.trim(),
      model: model.trim() || DEFAULT_SUMMARY_MODEL,
      enabled,
    });
    setValue("");
    setEditing(false);
  };

  return (
    <section className="panel">
      <div className="panel-header">Anthropic (run summaries)</div>
      <div style={{ padding: 16, display: "grid", gap: 12 }}>
        <div style={{ color: "var(--muted)", fontSize: 13 }}>
          Used to summarize each completed run with Claude (default model{" "}
          <code>{DEFAULT_SUMMARY_MODEL}</code>). Summaries appear on the run detail
          page and as a one-line preview in the run list. The key never leaves
          the dashboard host.
        </div>

        {q.isLoading ? (
          <div style={{ color: "var(--muted)" }}>Loading…</div>
        ) : q.data?.configured ? (
          <div
            style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}
          >
            <span>
              Configured: <code>{q.data.masked_api_key}</code>
            </span>
            <span>
              Model: <code>{q.data.model ?? "(default)"}</code>
            </span>
            <span>
              Auto-summarize:{" "}
              <code>{q.data.enabled === false ? "off" : "on"}</code>
            </span>
            <button
              type="button"
              onClick={() => {
                setModel(q.data?.model ?? DEFAULT_SUMMARY_MODEL);
                setEnabled(q.data?.enabled !== false);
                setEditing(true);
              }}
              style={smallBtn}
            >
              Rotate / edit
            </button>
            <button
              type="button"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
              style={{ ...smallBtn, color: "var(--err)" }}
            >
              Delete
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <span style={{ color: "var(--muted)" }}>Not configured.</span>
            <button type="button" onClick={() => setEditing(true)} style={smallBtn}>
              Add key
            </button>
          </div>
        )}

        {remove.error && (
          <div style={{ color: "var(--err)", fontSize: 12 }}>
            Failed to delete key: {String((remove.error as Error).message)}
          </div>
        )}

        {editing && (
          <form onSubmit={onSubmit} style={{ display: "grid", gap: 8, maxWidth: 480 }}>
            <input
              type="password"
              placeholder="sk-ant-…"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoFocus
              style={inputStyle}
            />
            <input
              type="text"
              placeholder={`model (e.g. ${DEFAULT_SUMMARY_MODEL})`}
              value={model}
              onChange={(e) => setModel(e.target.value)}
              style={inputStyle}
            />
            <label style={{ fontSize: 13, color: "var(--muted)" }}>
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />{" "}
              Auto-summarize each completed run
            </label>
            {save.error && (
              <div style={{ color: "var(--err)", fontSize: 12 }}>
                {String((save.error as Error).message)}
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button type="submit" disabled={save.isPending} style={primaryBtn}>
                {save.isPending ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setValue("");
                }}
                style={smallBtn}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}


// --- GitHub token --------------------------------------------------------

function GithubTokenPanel() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: TOKEN_KEY,
    queryFn: () => api.getGithubTokenSetting(),
  });

  const save = useMutation({
    mutationFn: (token: string) => api.setGithubTokenSetting(token),
    onSuccess: () => qc.invalidateQueries({ queryKey: TOKEN_KEY }),
  });
  const remove = useMutation({
    mutationFn: () => api.deleteGithubTokenSetting(),
    onSuccess: () => qc.invalidateQueries({ queryKey: TOKEN_KEY }),
  });

  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (value.trim().length < 20) return;
    await save.mutateAsync(value.trim());
    setValue("");
    setEditing(false);
  };

  return (
    <section className="panel">
      <div className="panel-header">GitHub PAT</div>
      <div style={{ padding: 16, display: "grid", gap: 12 }}>
        <div style={{ color: "var(--muted)", fontSize: 13 }}>
          Used to fetch commits, open PRs, and the latest GitHub Actions run
          per repo on the /repos view. Read-only access to the relevant repos
          is enough. Fine-grained PAT or classic PAT with{" "}
          <code>repo:read</code> + <code>actions:read</code>.
        </div>

        {q.isLoading ? (
          <div style={{ color: "var(--muted)" }}>Loading…</div>
        ) : q.data?.configured ? (
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <span>
              Configured: <code>{q.data.masked}</code>
            </span>
            <button
              type="button"
              onClick={() => setEditing(true)}
              style={smallBtn}
            >
              Rotate
            </button>
            <button
              type="button"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
              style={{ ...smallBtn, color: "var(--err)" }}
            >
              Delete
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <span style={{ color: "var(--muted)" }}>Not configured.</span>
            <button type="button" onClick={() => setEditing(true)} style={smallBtn}>
              Add token
            </button>
          </div>
        )}

        {remove.error && (
          <div style={{ color: "var(--err)", fontSize: 12 }}>
            Failed to delete token: {String((remove.error as Error).message)}
          </div>
        )}

        {editing && (
          <form onSubmit={onSubmit} style={{ display: "grid", gap: 8, maxWidth: 480 }}>
            <input
              type="password"
              placeholder="ghp_… or github_pat_…"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoFocus
              style={inputStyle}
            />
            {save.error && (
              <div style={{ color: "var(--err)", fontSize: 12 }}>
                {String((save.error as Error).message)}
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button type="submit" disabled={save.isPending} style={primaryBtn}>
                {save.isPending ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setValue("");
                }}
                style={smallBtn}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

// --- Dokploy -------------------------------------------------------------

function DokployPanel() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: DOKPLOY_KEY,
    queryFn: () => api.getDokploySetting(),
  });

  const save = useMutation({
    mutationFn: ({ api_url, api_key }: { api_url: string; api_key: string }) =>
      api.setDokploySetting(api_url, api_key),
    onSuccess: () => qc.invalidateQueries({ queryKey: DOKPLOY_KEY }),
  });
  const remove = useMutation({
    mutationFn: () => api.deleteDokploySetting(),
    onSuccess: () => qc.invalidateQueries({ queryKey: DOKPLOY_KEY }),
  });

  const [editing, setEditing] = useState(false);
  const [apiUrl, setApiUrl] = useState("");
  const [apiKey, setApiKey] = useState("");

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (apiUrl.trim().length < 8 || apiKey.trim().length < 8) return;
    await save.mutateAsync({
      api_url: apiUrl.trim(),
      api_key: apiKey.trim(),
    });
    setApiUrl("");
    setApiKey("");
    setEditing(false);
  };

  return (
    <section className="panel">
      <div className="panel-header">Dokploy API</div>
      <div style={{ padding: 16, display: "grid", gap: 12 }}>
        <div style={{ color: "var(--muted)", fontSize: 13 }}>
          Used to surface the current compose status next to each repo on
          /repos (the green/red <code>DEPLOY</code> pill). Generate an API
          key in Dokploy → Settings → API. The URL is your Dokploy base,
          e.g. <code>https://dokploy.example.com</code>.
        </div>

        {q.isLoading ? (
          <div style={{ color: "var(--muted)" }}>Loading…</div>
        ) : q.data?.configured ? (
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <span>
              URL: <code>{q.data.api_url}</code>
            </span>
            <span>
              Key: <code>{q.data.masked_api_key}</code>
            </span>
            <button
              type="button"
              onClick={() => {
                setApiUrl(q.data?.api_url ?? "");
                setEditing(true);
              }}
              style={smallBtn}
            >
              Rotate
            </button>
            <button
              type="button"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
              style={{ ...smallBtn, color: "var(--err)" }}
            >
              Delete
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <span style={{ color: "var(--muted)" }}>Not configured.</span>
            <button type="button" onClick={() => setEditing(true)} style={smallBtn}>
              Add
            </button>
          </div>
        )}

        {remove.error && (
          <div style={{ color: "var(--err)", fontSize: 12 }}>
            Failed to delete Dokploy config:{" "}
            {String((remove.error as Error).message)}
          </div>
        )}

        {editing && (
          <form onSubmit={onSubmit} style={{ display: "grid", gap: 8, maxWidth: 480 }}>
            <input
              type="text"
              placeholder="https://dokploy.example.com"
              value={apiUrl}
              onChange={(e) => setApiUrl(e.target.value)}
              autoFocus
              style={inputStyle}
            />
            <input
              type="password"
              placeholder="API key"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              style={inputStyle}
            />
            {save.error && (
              <div style={{ color: "var(--err)", fontSize: 12 }}>
                {String((save.error as Error).message)}
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button type="submit" disabled={save.isPending} style={primaryBtn}>
                {save.isPending ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setApiUrl("");
                  setApiKey("");
                }}
                style={smallBtn}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

// --- Agents --------------------------------------------------------------

function AgentsPanel() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: AGENTS_KEY,
    queryFn: () => api.listAgents(),
    refetchInterval: 15_000,
  });
  const remove = useMutation({
    mutationFn: (agentId: string) => api.deleteAgent(agentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: AGENTS_KEY }),
  });

  return (
    <section className="panel">
      <div className="panel-header">Host agents</div>
      <div style={{ padding: 16, display: "grid", gap: 16 }}>
        <div style={{ color: "var(--muted)", fontSize: 13 }}>
          Register one agent per machine that owns cron jobs you want to
          surface. The agent snapshots that host's crontab every 30 s and
          applies pause/resume/run-now/set-schedule commands within 5 s.
          Install instructions live in{" "}
          <code>tools/host-agent/README.md</code>.
        </div>

        {q.isLoading ? (
          <div style={{ color: "var(--muted)" }}>Loading…</div>
        ) : q.data && q.data.agents.length > 0 ? (
          <table className="mobile-cards">
            <thead>
              <tr>
                <th>agent_id</th>
                <th>hostname</th>
                <th>last seen</th>
                <th>registered</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {q.data.agents.map((a) => (
                <tr key={a.agent_id}>
                  <td className="mc-title">
                    <code>{a.agent_id}</code>
                  </td>
                  <td data-label="Hostname">{a.hostname}</td>
                  <td
                    data-label="Last seen"
                    style={{ color: a.last_seen_at ? "var(--text)" : "var(--warn)" }}
                  >
                    {a.last_seen_at ? <Timestamp iso={a.last_seen_at} /> : "never"}
                  </td>
                  <td data-label="Registered" style={{ color: "var(--muted)" }}>
                    <Timestamp iso={a.created_at} />
                  </td>
                  <td className="mc-actions">
                    <button
                      type="button"
                      disabled={remove.isPending}
                      style={{ ...smallBtn, color: "var(--err)" }}
                      onClick={() => {
                        if (
                          !confirm(
                            `Delete agent "${a.agent_id}"? Its rows in repos/cron_jobs/cron_commands are kept; the agent simply can't authenticate any more until re-registered.`,
                          )
                        )
                          return;
                        remove.mutate(a.agent_id);
                      }}
                    >
                      {remove.isPending && remove.variables === a.agent_id
                        ? "Deleting…"
                        : "Delete"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={{ color: "var(--muted)" }}>No agents registered yet.</div>
        )}

        {remove.error && (
          <div style={{ color: "var(--err)", fontSize: 12 }}>
            Failed to delete agent: {String((remove.error as Error).message)}
          </div>
        )}

        <RegisterAgentForm />
      </div>
    </section>
  );
}

function RegisterAgentForm() {
  const qc = useQueryClient();
  const [agentId, setAgentId] = useState("");
  const [hostname, setHostname] = useState("");
  const [secret, setSecret] = useState<{
    agent_id: string;
    hostname: string;
    token: string;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  const register = useMutation({
    mutationFn: ({ agent_id, hostname }: { agent_id: string; hostname: string }) =>
      api.registerAgent(agent_id, hostname),
    onSuccess: (data) => {
      setSecret(data);
      setAgentId("");
      setHostname("");
      void qc.invalidateQueries({ queryKey: AGENTS_KEY });
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    register.mutate({ agent_id: agentId.trim(), hostname: hostname.trim() });
  };

  if (secret) {
    const envFile = [
      `AGENT_ID=${secret.agent_id}`,
      `AGENT_TOKEN=${secret.token}`,
      `DASHBOARD_URL=${window.location.origin}`,
    ].join("\n");
    return (
      <div
        style={{
          padding: 12,
          border: "1px solid var(--accent)",
          borderRadius: 6,
          background: "rgba(88,166,255,0.05)",
          display: "grid",
          gap: 8,
        }}
      >
        <div style={{ fontWeight: 600 }}>
          Agent registered — save this once
        </div>
        <div style={{ color: "var(--muted)", fontSize: 12 }}>
          The token is shown only this once. Paste it into{" "}
          <code>/etc/agentdash-host-agent.env</code> on the host, then{" "}
          <code>systemctl restart agentdash-host-agent</code>.
        </div>
        <pre
          style={{
            background: "var(--bg)",
            border: "1px solid var(--border)",
            padding: 12,
            borderRadius: 4,
            margin: 0,
            fontSize: 12,
            overflowX: "auto",
          }}
        >
{envFile}
        </pre>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            style={smallBtn}
            onClick={() => {
              navigator.clipboard?.writeText(envFile).then(
                () => {
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1200);
                },
                () => {},
              );
            }}
          >
            {copied ? "Copied" : "Copy"}
          </button>
          <button type="button" style={smallBtn} onClick={() => setSecret(null)}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} style={{ display: "grid", gap: 8, maxWidth: 480 }}>
      <div style={{ fontWeight: 600 }}>Register a new agent</div>
      <input
        placeholder="agent_id (e.g. homelab)"
        value={agentId}
        onChange={(e) => setAgentId(e.target.value)}
        required
        style={inputStyle}
      />
      <input
        placeholder="hostname (display name)"
        value={hostname}
        onChange={(e) => setHostname(e.target.value)}
        required
        style={inputStyle}
      />
      {register.error && (
        <div style={{ color: "var(--err)", fontSize: 12 }}>
          {String((register.error as Error).message)}
        </div>
      )}
      <div>
        <button
          type="submit"
          disabled={register.isPending || !agentId || !hostname}
          style={primaryBtn}
        >
          {register.isPending ? "Generating token…" : "Generate token"}
        </button>
      </div>
    </form>
  );
}

const inputStyle: React.CSSProperties = {
  background: "var(--bg)",
  color: "var(--text)",
  border: "1px solid var(--border)",
  borderRadius: 4,
  padding: "8px 10px",
  fontSize: 13,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
};

const smallBtn: React.CSSProperties = {
  background: "transparent",
  color: "var(--muted)",
  border: "1px solid var(--border)",
  borderRadius: 4,
  padding: "4px 10px",
  fontSize: 12,
  cursor: "pointer",
};

const primaryBtn: React.CSSProperties = {
  background: "var(--accent)",
  color: "#0e1116",
  border: "none",
  borderRadius: 4,
  padding: "6px 14px",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
};
